/**
 * Service for managing deployed APIs.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface DeployedAPI {
  id: string;
  user_id: string;
  pipeline_id: string;
  api_key: string;
  endpoint_path: string;  // API endpoint path (e.g., /api/deploy/deployed/{id})
  api_url_path?: string;  // Legacy field, use endpoint_path instead
  api_url?: string;  // Full API URL (added by backend)
  status: 'active' | 'inactive';
  runpod_s3_path?: string;  // Changed from runpod_storage_path
  created_at?: string;
  updated_at?: string;
  name?: string; // API name
  s3_base_path?: string; // S3 base path for compatibility
}

export interface DeployPipelineResponse {
  api_config: DeployedAPI;
  api_url: string;
  api_key: string;
}

class APIService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * Deploy a pipeline as an API.
   */
  async deployPipeline(pipelineId: string): Promise<DeployPipelineResponse> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/deploy/pipeline/${pipelineId}`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to deploy pipeline');
    }

    return response.json();
  }

  /**
   * List all deployed APIs for the current user.
   */
  async listDeployedAPIs(): Promise<DeployedAPI[]> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/deploy/list`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list deployed APIs');
    }

    const data = await response.json();
    return data.apis || [];
  }

  /**
   * Undeploy/remove an API.
   */
  async undeployAPI(apiConfigId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/deploy/undeploy/${apiConfigId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to undeploy API');
    }
  }

  /**
   * Get the backend URL for API testing based on environment configuration.
   */
  async getBackendUrlForTesting(): Promise<string> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/deploy/backend-url`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        return data.backend_url || BACKEND_URL;
      }
    } catch (error) {
      console.warn('Failed to fetch backend URL from config, using default:', error);
    }
    return BACKEND_URL;
  }

  /**
   * Generate test command strings for a deployed API.
   */
  async getAPITestCommand(apiConfig: DeployedAPI, baseUrl?: string): Promise<{ powershell: string; curl: string }> {
    // Determine the correct backend URL
    let backendUrlForTesting: string;
    if (baseUrl) {
      backendUrlForTesting = baseUrl;
    } else if (apiConfig.api_url) {
      // Extract base URL from full API URL
      const urlObj = new URL(apiConfig.api_url);
      backendUrlForTesting = `${urlObj.protocol}//${urlObj.host}`;
    } else {
      // Fetch from backend config based on run_mode
      backendUrlForTesting = await this.getBackendUrlForTesting();
    }
    
    // Use api_url if available (already includes full backend URL), otherwise construct it
    const endpointPath = apiConfig.endpoint_path || apiConfig.api_url_path || '';
    const apiUrl = apiConfig.api_url 
      || `${backendUrlForTesting}${endpointPath}`;
    
    const powershell = `Invoke-RestMethod -Uri "${apiUrl}" -Method POST -Headers @{"Authorization"="Bearer ${apiConfig.api_key}"} -InFile "./test.jpg"`;
    
    const curl = `curl -X POST "${apiUrl}" -H "Authorization: Bearer ${apiConfig.api_key}" --data-binary @test.jpg`;
    
    return { powershell, curl };
  }
}

export const apiService = new APIService();
export default apiService;

